import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

function flattenPlannedWorkouts(weeks) {
  if (!Array.isArray(weeks)) return [];
  const out = [];
  for (const week of weeks) {
    const ws = Array.isArray(week?.workouts) ? week.workouts : [];
    for (const w of ws) {
      if (!w) continue;
      out.push({ ...w, week_number: week?.week_number });
    }
  }
  return out;
}

function formatDayLabel(t, dayName) {
  const key = String(dayName || '').toLowerCase();
  if (!key) return '';
  return t(`days.${key}`, { defaultValue: dayName });
}

function WorkoutCard({ label, workout, onPress, dotClass, dayLabel }) {
  if (!workout) return null;

  const plannedKm = Number(workout?.distance_km || 0);
  const plannedMin = Number(workout?.duration_min || 0);
  const plannedElev = Number(workout?.altitude_gain_m || 0);

  return (
    <div className="mb-3 last:mb-0">
      <div className="text-xs font-extrabold text-gray-900 mb-2">{label}</div>
      <button
        type="button"
        onClick={onPress}
        className="w-full text-left bg-white border border-gray-200 rounded-xl shadow-sm hover:bg-gray-50 active:scale-[0.99] transition p-3"
      >
        <div className="flex items-start gap-3">
          <div className={`mt-1 w-3 h-3 rounded-full ${dotClass || 'bg-gray-300'}`} />
          <div className="min-w-0 flex-1">
            <div className="font-extrabold text-gray-900 truncate">
              {workout?.title || '(Untitled)'}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {dayLabel ? `${dayLabel} · ` : ''}
              <span className="font-mono">{workout?.date || ''}</span>
            </div>

            {(plannedKm > 0 || plannedMin > 0 || plannedElev > 0) ? (
              <div className="mt-2 flex flex-wrap gap-2">
                {plannedKm > 0 ? (
                  <span className="px-2 py-1 rounded bg-blue-100 text-blue-800 text-xs font-extrabold tabular-nums">
                    {Math.round(plannedKm)} km
                  </span>
                ) : null}
                {plannedMin > 0 ? (
                  <span className="px-2 py-1 rounded bg-green-100 text-green-800 text-xs font-extrabold tabular-nums">
                    {Math.round(plannedMin)} min
                  </span>
                ) : null}
                {plannedElev > 0 ? (
                  <span className="px-2 py-1 rounded bg-yellow-100 text-yellow-800 text-xs font-extrabold tabular-nums">
                    +{Math.round(plannedElev)} m
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </button>
    </div>
  );
}

export default function FeaturedWorkoutsBlock({
  weeks,
  todayDateISO,
  workoutColors,
  onWorkoutPress
}) {
  const { t } = useTranslation();

  const { todayWorkout, nextWorkout } = useMemo(() => {
    const all = flattenPlannedWorkouts(weeks)
      .filter((w) => w?.date && String(w.type || '').toLowerCase() !== 'rest');

    const today = all.find((w) => String(w.date) === String(todayDateISO)) || null;

    const next =
      all
        .filter((w) => String(w.date) > String(todayDateISO))
        .sort((a, b) => String(a.date).localeCompare(String(b.date)))[0] || null;

    return { todayWorkout: today, nextWorkout: next };
  }, [weeks, todayDateISO]);

  // If there's nothing to feature, keep UI clean.
  if (!todayWorkout && !nextWorkout) return null;

  return (
    <div className="mb-6">
      <WorkoutCard
        label={t('plan.featured_workouts.today')}
        workout={todayWorkout}
        dayLabel={formatDayLabel(t, todayWorkout?.day)}
        dotClass={todayWorkout ? workoutColors?.[todayWorkout.type] : null}
        onPress={() => {
          if (!todayWorkout) return;
          if (typeof onWorkoutPress === 'function') onWorkoutPress(todayWorkout, todayWorkout.week_number);
        }}
      />

      <WorkoutCard
        label={t('plan.featured_workouts.next')}
        workout={nextWorkout}
        dayLabel={formatDayLabel(t, nextWorkout?.day)}
        dotClass={nextWorkout ? workoutColors?.[nextWorkout.type] : null}
        onPress={() => {
          if (!nextWorkout) return;
          if (typeof onWorkoutPress === 'function') onWorkoutPress(nextWorkout, nextWorkout.week_number);
        }}
      />
    </div>
  );
}

